import { Injectable, PLATFORM_ID, Renderer2, RendererFactory2, RendererType2, inject, ɵJSACTION_BLOCK_ELEMENT_MAP } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ɵDomRendererFactory2 as DomRendererFactory2 } from '@angular/platform-browser';
import { releaseReplaySubtree } from './utils/replay-node-cleanup';

const PATCHED = Symbol('mc-ionic-class-safe');

/**
 * Al hidratar, Angular vuelve a escribir el `class` ESTÁTICO de cada elemento de la
 * plantilla (writeDirectClass → renderer.setAttribute('class', …)). Como Ionic ya ha
 * definido sus custom elements y Stencil ha renderizado los del SSR (con
 * IonicModule la cola de Stencil es síncrona), esa escritura borra las clases que
 * Stencil añade al host: `hydrated` (sin ella Ionic aplica visibility:hidden),
 * `md`/`ios`, `button-clear`, `ion-activatable`… Qué elemento la pierde depende de
 * la carrera entre la llegada del entry chunk de Ionic y la hidratación de la ruta
 * lazy (medido 02/09: 32 ion-icon ocultos en el home, 6 ion-button y toda la ficha
 * ocultos). Este factory envuelve el renderer del DOM y, SOLO para elementos ion-*
 * y SOLO en el navegador, fusiona: escribe el class de la plantilla y vuelve a
 * añadir las clases que ya tenía el nodo. Los bindings dinámicos ([class.x],
 * ngClass) usan addClass/removeClass y no pasan por aquí.
 */
@Injectable()
export class IonicClassSafeRendererFactory implements RendererFactory2 {
  // any: begin/end/whenRenderingDone no forman parte del tipo público de DomRendererFactory2
  private base: any = inject(DomRendererFactory2);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // Angular 22 private contract, isolated here like DomRendererFactory2 above.
  // Recheck on framework upgrades; removing live entries would break event replay.
  private replayBlocks = inject(ɵJSACTION_BLOCK_ELEMENT_MAP, { optional: true });

  createRenderer(hostElement: any, type: RendererType2 | null): Renderer2 {
    const renderer: any = this.base.createRenderer(hostElement, type);
    if (this.isBrowser && !renderer[PATCHED]) {
      renderer[PATCHED] = true;
      if (this.replayBlocks) {
        const blocks = this.replayBlocks;
        const destroyNode = renderer.destroyNode;
        renderer.destroyNode = function (node: any) {
          releaseReplaySubtree(blocks, node);
          if (destroyNode) destroyNode.call(this, node);
        };
      }
      const original = renderer.setAttribute;
      renderer.setAttribute = function (el: any, name: string, value: string, namespace?: string | null) {
        if (name === 'class' && el && typeof el.tagName === 'string' && el.tagName.indexOf('ION-') === 0 && el.classList && el.classList.length) {
          const keep: string[] = Array.prototype.slice.call(el.classList);
          original.call(this, el, name, value, namespace);
          for (const c of keep) el.classList.add(c);
          return;
        }
        return original.call(this, el, name, value, namespace);
      };
    }
    return renderer;
  }
  begin(): void { if (this.base.begin) this.base.begin(); }
  end(): void { if (this.base.end) this.base.end(); }
  whenRenderingDone(): Promise<any> { return this.base.whenRenderingDone ? this.base.whenRenderingDone() : Promise.resolve(); }
}
